import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import type { DisplaysSearch } from '@/features/displays/lib/search'
import { AlertTriangle, ArrowRight, CalendarClock, FileClock, HardDrive, MapPin, Megaphone, Monitor, Plus, UserPlus } from 'lucide-react'
import { Can } from '@/components/can'
import { EmptyState } from '@/components/empty-state'
import { Page } from '@/components/page-header'
import { QueryError } from '@/components/query-state'
import { StatusDot } from '@/components/status-dot'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Meter } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { ActivityItem } from '@/features/audit/components/activity-item'
import { useAuthedMe } from '@/features/auth/api'
import { UsageList } from '@/features/billing/components/usage-list'
import { useOnboarding } from '@/features/onboarding/api'
import { usePermission } from '@/hooks/use-permission'
import { formatBytes, formatDate, usageRatio, usageTone } from '@/lib/format'
import { useDashboard } from '../api'
import { DeviceHealthCard } from '../components/device-health'
import { KpiCard } from '../components/kpi-card'
import { AlertsCard, CurrentlyPlayingCard, PlayerVersionsCard, UpcomingCampaignsCard } from '../components/operations-cards'
import { OnboardingChecklist } from '../components/onboarding-checklist'

function greeting(date = new Date()) {
  const h = date.getHours()
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
}

export function DashboardPage() {
  const me = useAuthedMe()
  const dashboard = useDashboard()
  const onboarding = useOnboarding()
  const canAudit = usePermission('audit.view')
  const canViewDisplays = usePermission('displays.view')
  const canViewMedia = usePermission('media.view')
  const canPlan = usePermission(['campaigns.manage', 'schedules.manage'])
  const ob = onboarding.data ?? me.onboarding
  const showChecklist = !!ob && !ob.completed && !ob.dismissed
  const d = dashboard.data
  const firstName = me.user.name.split(' ')[0]

  return (
    <Page>
      <div className="flex flex-col gap-4 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[13px] text-muted-foreground">{formatDate(new Date().toISOString(), { weekday: 'long', month: 'long', day: 'numeric' })}</p>
          <h1 className="text-2xl font-semibold tracking-tight">
            {greeting()}, {firstName}
          </h1>
        </div>
        <div className="flex gap-2">
          <Can permission="users.manage">
            <Button variant="outline" asChild>
              <Link to="/users" search={{ tab: 'invitations', invite: true }}>
                <UserPlus /> Invite
              </Link>
            </Button>
          </Can>
          <Can permission="locations.manage">
            <Button asChild>
              <Link to="/locations">
                <Plus /> Add location
              </Link>
            </Button>
          </Can>
        </div>
      </div>

      {dashboard.isError ? (
        <Card>
          <QueryError error={dashboard.error} onRetry={() => void dashboard.refetch()} />
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 2xl:grid-cols-6">
          {!d ? (
            Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-[108px] rounded-xl" />)
          ) : (
            <>
              <MaybeLink enabled={canViewDisplays} search={{ connectivity: 'online' }} label="View online displays">
                <KpiCard
                  className="h-full"
                  label="Displays online"
                  icon={Monitor}
                  accent={d.displays.total > 0 && d.displays.online === d.displays.total ? 'success' : 'primary'}
                  value={
                    <span>
                      {d.displays.online}
                      <span className="text-lg text-muted-foreground"> / {d.displays.total}</span>
                    </span>
                  }
                  footer={
                    <span className="flex items-center gap-1.5">
                      <StatusDot status={d.displays.total === 0 ? 'offline' : d.displays.online === d.displays.total ? 'online' : 'warning'} pulse={d.displays.online > 0} />
                      {d.displays.total === 0 ? 'No displays yet' : `${d.displays.total - d.displays.online} offline`}
                    </span>
                  }
                />
              </MaybeLink>
              <KpiCard label="Locations" icon={MapPin} accent="cyan" value={d.locations} footer={<Link to="/locations" className="hover:text-foreground">View locations →</Link>} />
              {canViewMedia ? (
                <Link to="/media" title="Open the media library" className="block h-full rounded-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring [&>[data-slot=card]]:hover:border-primary/30">
                  <StorageKpi used={d.storage.used_bytes} limit={d.storage.limit_bytes} linked />
                </Link>
              ) : (
                <StorageKpi used={d.storage.used_bytes} limit={d.storage.limit_bytes} />
              )}
              <KpiCard label="Active campaigns" icon={Megaphone} accent="primary" value={d.active_campaigns} footer={d.upcoming_campaigns?.length ? `${d.upcoming_campaigns.length} starting this week` : 'Running today'} />
              <KpiCard label="Active schedules" icon={CalendarClock} accent="cyan" value={d.active_schedules} footer="Across all displays" />
              <MaybeLink enabled={canViewDisplays} search={{ has_warnings: true }} label="View displays with warnings">
                <KpiCard
                  className="h-full"
                  label="Players with warnings"
                  icon={AlertTriangle}
                  accent={d.players_with_warnings > 0 ? 'warning' : 'muted'}
                  value={d.players_with_warnings}
                  footer={
                    <span className="flex items-center gap-1.5">
                      <StatusDot status={d.players_with_warnings > 0 ? 'warning' : 'online'} />
                      {d.players_with_warnings > 0 ? 'Needs attention' : 'All healthy'}
                    </span>
                  }
                />
              </MaybeLink>
            </>
          )}
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="grid content-start gap-6 lg:col-span-2">
          {showChecklist && <OnboardingChecklist onboarding={ob} />}
          {canViewDisplays && (d?.displays.total ?? 0) > 0 && <CurrentlyPlayingCard items={d?.currently_playing} online={d?.displays.online} />}
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <div>
                <CardTitle>Recent activity</CardTitle>
                <p className="text-[13px] text-muted-foreground">What your team changed lately</p>
              </div>
              {canAudit && (
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/audit-log">
                    View all <ArrowRight />
                  </Link>
                </Button>
              )}
            </CardHeader>
            <CardContent className="pb-2">
              {!d ? (
                <div className="space-y-4 py-2">
                  {Array.from({ length: 4 }, (_, i) => (
                    <div key={i} className="flex gap-3">
                      <Skeleton className="size-8 rounded-full" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3.5 w-2/3" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : d.recent_activity.length === 0 ? (
                <EmptyState compact icon={FileClock} title="No activity yet" description="Changes to locations, team and settings will show up here." />
              ) : (
                <ul className="divide-y">
                  {d.recent_activity.slice(0, 8).map((log) => (
                    <ActivityItem key={log.id} log={log} />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid content-start gap-6">
          <AlertsCard alerts={d?.alerts} />
          <DeviceHealthCard totalDisplays={d?.displays.total} />
          {canPlan && <UpcomingCampaignsCard campaigns={d?.upcoming_campaigns} />}
          {canViewDisplays && <PlayerVersionsCard versions={d?.player_versions} />}
          {me.subscription && (
            <Card>
              <CardHeader className="flex-row items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Current plan</p>
                  <CardTitle className="mt-0.5 text-lg">{me.subscription.plan.name}</CardTitle>
                </div>
                <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap text-primary capitalize">
                  {me.subscription.status === 'trialing' ? `Trial · ${formatDate(me.subscription.trial_ends_at, { month: 'short', day: 'numeric' })}` : me.subscription.status.replace('_', ' ')}
                </span>
              </CardHeader>
              <CardContent>
                <UsageList entitlements={me.subscription.entitlements} keys={['displays', 'locations', 'users', 'storage_bytes']} compact />
                <Button variant="outline" size="sm" className="mt-5 w-full" asChild>
                  <Link to="/settings/subscription">Manage subscription</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Page>
  )
}

function StorageKpi({ used, limit, linked = false }: { used: number; limit: number | null; linked?: boolean }) {
  const ratio = usageRatio(used, limit)
  const tone = usageTone(ratio)
  return (
    <KpiCard
      className={linked ? 'h-full' : undefined}
      label="Storage used"
      icon={HardDrive}
      accent={tone === 'critical' ? 'danger' : tone === 'warning' ? 'warning' : 'primary'}
      value={formatBytes(used)}
      footer={
        <div className="space-y-1.5">
          <Meter value={ratio} tone={tone === 'ok' ? 'brand' : tone} size="sm" label="Storage usage" />
          <span className="flex items-center justify-between gap-2">
            {limit === null ? 'Unlimited storage' : `of ${formatBytes(limit)}`}
            {linked && <span className="text-muted-foreground transition-colors group-hover:text-foreground">Media →</span>}
          </span>
        </div>
      }
    />
  )
}

/** Makes a KPI tile a link into the filtered displays list (when the user can see displays). */
function MaybeLink({ enabled, search, label, children }: { enabled: boolean; search: DisplaysSearch; label: string; children: ReactNode }) {
  if (!enabled) return <>{children}</>
  return (
    <Link to="/displays" search={search} title={label} className="block h-full rounded-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring [&>[data-slot=card]]:hover:border-primary/30">
      {children}
    </Link>
  )
}
